import { DateTime } from 'luxon';
import { Omit } from 'react-router';

export enum AlertType {
  QAAlert = 'qa_alert',
  PriceRecall = 'price_recall',
}

export enum UserType {
  Admin = 'admin',
  LCBO = 'lcbo',
  Grocery = 'grocery', 
  LCBOReporter = 'lcboReporter', 
  GrocerReporter = 'grocerReporter', 
  Inactive = 'inactive', 
  QAAdmin = 'qaAdmin',
}

export namespace UserType {
  const assignableUserTypes: ReadonlySet<UserType> = new Set([
    UserType.Admin,
    UserType.LCBO,
  ]);
  export function isAssignable(user: UserType): boolean {
    return assignableUserTypes.has(user);
  }

  export function canFetchAllUsers(user: UserType): boolean {
    // Currently, only users that can be assigned to claims can view who is assigned to claims
    return assignableUserTypes.has(user);
  }

  const usersWhoViewClaimDetails: ReadonlySet<UserType> = new Set([
    UserType.Admin,
    UserType.QAAdmin,
    UserType.LCBO,
    UserType.LCBOReporter,
  ]);
  export function canViewClaimDetails(user: User): boolean {
    return usersWhoViewClaimDetails.has(user.type);
  }

  /**
   * Gets the alert types which the given user deals with.
   * Note that this doesn't mean that the user isn't authorized to view/modify
   * other types of alerts, but this just indicates which ones should show by default
   * for the given user type.
   */
  export function alertType(user: UserType): ProductAlert['type'][] {
    let type: AlertType[] = [];
    switch (user) {
      case UserType.LCBO:
      case UserType.LCBOReporter:
      case UserType.Admin:
        type = [AlertType.PriceRecall, AlertType.QAAlert];
        break;
      case UserType.QAAdmin:
        type = [AlertType.QAAlert];
        break;
      default:
        break;
    }
    return type;
  }

  export function shouldFetchRefundClaims(user: User): boolean {
    return user.type !== UserType.QAAdmin;
  }

  export function shouldFetchAdjustmentClaims(user: User): boolean {
    return user.type !== UserType.QAAdmin;
  }
}

export type ClaimType =
  | 'refund'
  | 'adjustment'
  /* Ensure that this string is the same as AlertClaim.TYPE_NAME
  In new TypeScript versions, this can be defined as a constant and this wouldn't be a problem. */
  | 'recall';

/* tslint:disable-next-line */
export function transformPhoto(photo: any): Photo {
  photo.createDate = DateTime.fromISO(photo.createDate);
  photo.expiryDate = DateTime.fromISO(photo.expiryDate);
  photo.updateDate = DateTime.fromISO(photo.updateDate);
  return photo;
}

export interface Photo {
  createDate: DateTime;
  expiryDate: DateTime;
  guid: string;
  id: number;
  mimetype: string;
  name: string;
  updateDate: DateTime;
  url: string;
}

/* tslint:disable-next-line */
export function transformStore(store: any): Store {
  store.createDate = DateTime.fromISO(store.createDate);
  store.updateDate = DateTime.fromISO(store.updateDate);
  return store;
}

export interface Store {
  id: number;
  storeOperatorNumber: number;
  storeOperatorName: string;
  address: string;
  city: string;
  phoneNumber: string;
  postalCode: string;
  createDate: DateTime;
  updateDate: DateTime;
  channelId: number | null;
  channelName: string;
}

export interface Channel {
  id: number;
  channelName: string;
  users: User[];
  storesList: string;
}

/* tslint:disable-next-line */
export function transformUser(user: any): User {
  user.createDate = DateTime.fromISO(user.createDate);
  user.updateDate = DateTime.fromISO(user.updateDate);
  return user;
}

export interface User {
  type: UserType;
  createDate: DateTime;
  updateDate: DateTime;
  email: string;
  id: number;
  password: string;
  contactName: string;
  store: Store;
  channels: Channel[];
  channelNames: string;
  storeId: Store['id'];
}

/* tslint:disable-next-line */
export function transformComment(comment: any): Comment {
  comment.createDate = DateTime.fromISO(comment.createDate);
  comment.updateDate = DateTime.fromISO(comment.updateDate);
  comment.user = transformUser(comment.user);
  return comment;
}

export interface Comment {
  createDate: DateTime;
  updateDate: DateTime;
  message: string;
  id: number;
  status: ClaimType | null;
  user: User;
}

/* tslint:disable-next-line */
export function transformItem(item: any): Item {
  item.createDate = DateTime.fromISO(item.createDate);
  item.updateDate = DateTime.fromISO(item.updateDate);
  item.photos = item.photos ? item.photos.map(transformPhoto) : [];
  return item;
}

/* tslint:disable-next-line */
export function transformRefundItem(item: any): RefundItem {
  item = transformItem(item);
  item.receivalDate = DateTime.fromISO(item.receivalDate);
  return item;
}

/* tslint:disable-next-line */
export function transformAdjustmentItem(item: any): AdjustmentItem {
  return transformItem(item) as AdjustmentItem;
}

export interface Item {
  disposition?: 'destroy' | 'return' | undefined;
  claimCategory: 'short' | 'damage' | 'over' | null;
  createDate: DateTime;
  updateDate: DateTime;
  receivalDate: DateTime | null;
  id: number | string;
  isApproved: boolean | -1; // used as a loading state
  photos: Photo[];
  itemNumber: number;
  itemName?: string; // alias for productDescription
  purchaseNumber: number | null;
  productDescription: string;
  cases: number;
  units: number | undefined;
  unitsClaimed: number | undefined;
  alertNumber: ProductAlert['alertNumber'];
}

export interface RefundItem extends Item {
  units: number;
  cases: number;
  receivalDate: DateTime;
  reason: string;
}

export interface AdjustmentItem extends Item {
  itemReceived: boolean;
  unitsClaimed: number;
  skidsTied: number;
  casePositionOnSkid: string;
  caseSealed: boolean;
  breakageType: string;
  breakageLocation: string;
  driverAgree: boolean;
}

function itemString(items: any[]) {
  let itemList = '';
  items.forEach((item, index) => {
    itemList += item.itemNumber;
    if (index !== items.length - 1) {
      itemList += ', ';
    }
  });
  return itemList;
}
/* tslint:disable-next-line */
export function transformClaim<T extends Item>(
  type: ClaimType,
  claim: any
): Claim<T> {
  claim.createDate = DateTime.fromISO(claim.createDate);
  claim.updateDate = DateTime.fromISO(claim.updateDate);
  claim.goodsReceivedDate =
    claim.goodsReceivedDate && DateTime.fromISO(claim.goodsReceivedDate);
  claim.type = type;
  claim.date =
    claim.date && claim.date !== null
      ? DateTime.fromISO(claim.date)
      : claim.createDate;
  claim.photos = claim.photos ? claim.photos.map(transformPhoto) : [];
  claim.comments = claim.comments ?
    claim.comments.map(transformComment).sort((a, b) => a.createDate - b.createDate) :
    [];
  claim.items = claim.items ?
    claim.items.map(item => claim.type === 'refund' ? transformRefundItem(item) : transformAdjustmentItem(item)) :
    [];
  switch (claim.status) {
    case 'validating':
      claim.statusText = 'submitted for adjustment';
      break;
    case 'submitted':
      claim.statusText = 'lco submitted';
      break;
    default:
      claim.statusText = claim.status;
      break;
  }
  if (claim.status === 'submitted') {
    claim.updateDate = '-';
  }
  claim.itemList = claim.itemID ? claim.itemID : itemString(claim.items);
  claim.itemNumberList = claim.items ? claim.items.map(({itemNumber}) => (itemNumber > 0 ? itemNumber : 'Custom')).join(', ') : '';
  claim.itemDescriptionList = claim.items
    ? claim.items
        .map(({ itemName }) =>
          itemName && itemName !== null ? itemName : undefined
        )
        .join(', ')
    : '';
  claim.purchaseNumberList = claim.bolNumber ? claim.bolNumber : 'N/A';
  claim.returnInvoice = claim.return_invoice ? claim.return_invoice : claim.returnInvoice;
  return claim;
}

export interface Claim<T extends Item> {
  type: ClaimType;
  status:
    | 'draft'
    | 'submitted'
    | 'incomplete'
    | 'completed'
    | 'validating'
    | 'approved'
    | 'pending';
  statusText: string;
  createDate: DateTime;
  updateDate: DateTime;
  goodsReceivedDate: DateTime;
  id: number;
  photos: Photo[];
  comments: Comment[];
  items: T[];
  returnInvoice: string | null;
  date: DateTime;
  approvalRatio: string;
  storeId: number;
  userId: number;
  storeOperatorName: string;
  phoneNumber: string;
  address: string;
  contactName: string;
  email: string;
  bolNumber: string;
  carrierName: string;
  frozenLoad: boolean;
  sealIntact: boolean;
  sealNumber: string;
  assignedTo: number;
  assignedToString: string;
  completedBy: string;

  goodsReceivedDateString: string;
  createDateString: string;
  updateDateString: string;
  itemPO: string;
  category: string;
  notification: string;
  purchaseNumber: number;
  storeNumber: number;
  channelName: string;
  channelId: number;
  item: number;
  itemDescription: string;
  cases: number;
  units: number;
  decision: string;

  itemQuantity?: number;
  disposition?: 'destroy' | 'return' | undefined;
  alertNumber?: number;
  alert?: ProductAlert;

  alertId?: string;
  itemList?: string;
  completedByTitle?: string;
  storeEmail?: string;
  storeOwnerName?: string;
  storePhoneNumber?: string;
}

export enum ClaimStatus {
  Pending = 'pending',
  Incomplete = 'incomplete',
  Completed = 'completed',
  Submitted = 'submitted',
  Draft = 'draft',
  Validating = 'validating',
  Approved = 'approved',
}

export function mapClaimStatus(
  status: Claim<any>['status'] | ClaimStatus
): string {
  let statusText: string;
  switch (status) {
    case 'validating':
    case ClaimStatus.Validating:
      statusText = 'submitted for adjustment';
      break;
    case 'submitted':
    case ClaimStatus.Submitted:
      statusText = 'lco submitted';
      break;
    case 'approved':
    case ClaimStatus.Approved:
      statusText = 'approved by admin';
      break;
    default:
      statusText = status;
      break;
  }
  return statusText;
}

export enum ToastState {
  DEFAULT = 'default',
  SUCCESS = 'success',
  ERROR = 'error',
}

class TimestampedRecord {
  public created: DateTime;
  public updated: DateTime;

  public static applyFromRaw(
    values: RawTimestampedRecord,
    record: TimestampedRecord
  ): void {
    record.updated = DateTime.fromISO(values.updated);
    record.created = DateTime.fromISO(values.created);
  }
}

type RawTimestampedRecord = {
  created: string;
  updated: string;
};

export type RawResponse = {
  [key: string]: string | number | null | undefined;
};

export type SortSpecification<T> = Map<keyof T, 'asc' | 'desc'>;

export type RawAlert = {
  id: string;
  alertNumber: number;
  active: boolean;
  itemNumbers: number[];
  expiryDate: string;
  itemInfo: Product[];
  type: AlertType;
  lotCodes: string[];
  additionalInfo: string;
  isArchived: boolean;
  disposition: Disposition;
  groceryExclusiveItems: ProductInfo[];
} & RawTimestampedRecord;

/**
 * A product/item returned from the LCBO HQ API.
 * The item number is returned as a string unfortunately,
 * and it includes the item's name, making the existing Item type unsuitable for this.
 */
export type Product = {
  /** The name of the product. This would be null if the product wasn't found,
   * or if for any other reason the name couldn't be obtained.
   */
  itemName: string | null;
  itemNumber: string;
};

export type ProductInfo = {
  itemName: string;
  itemNumber: string;
  imageUrl: string;
  imageUrlThumb: string;
  productSize: string;
};

export type Disposition = 'destroy' | 'return' | 'both';
export class ProductAlert extends TimestampedRecord {
  public id: string;
  public alertNumber: number;
  public active: boolean;
  public expiryDate: DateTime;
  public itemInfo: Product[] = [];
  public type: AlertType;
  public lotCodes: string[];
  public additionalInfo: string;
  public isArchived: boolean;
  public disposition: Disposition;
  public itemNumbers?: number[];
  public groceryExclusiveItems: ProductInfo[];

  public static applyFromRaw(values: RawAlert, alert: ProductAlert): void {
    alert.id = values.id;
    alert.alertNumber = values.alertNumber;
    alert.active = values.active;
    alert.expiryDate = DateTime.fromISO(values.expiryDate);
    alert.itemInfo = values.itemInfo;
    alert.lotCodes = values.lotCodes;
    alert.additionalInfo = values.additionalInfo;
    alert.isArchived = values.isArchived;
    alert.disposition = values.disposition;
    alert.type = values.type;
    alert.groceryExclusiveItems = values.groceryExclusiveItems;
    TimestampedRecord.applyFromRaw(values, alert);
  }

  public static fromRaw(values: RawAlert): ProductAlert {
    const alert = new ProductAlert();
    ProductAlert.applyFromRaw(values, alert);
    return alert;
  }

  /**
   * Generates a default product alert, such as when an alert is being created.
   * @param user The user under which the alert is being created. This influences
   * the type of the alert.
   */
  public static defaultAlert(user?: User): ProductAlert {
    const alert = new ProductAlert();
    if (user) {
      const alertType = UserType.alertType(user.type);
      if (alertType !== null && alertType.length > 0) {
        alert.type = alertType[0];
      }
    }
    // Alerts are active by default.
    alert.active = true;
    return alert;
  }

  /**
   * Creates a shallow clone of this alert object.
   */
  public clone(): ProductAlert {
    const newAlert = new ProductAlert();
    Object.assign(newAlert, this);
    return newAlert;
  }

  public toRawPost(): Partial<RawAlert> {
    return {
      active: this.active,
      alertNumber: this.alertNumber,
      expiryDate: this.expiryDate ? this.expiryDate.toISO() : undefined,
      itemNumbers: this.itemInfo.map(({ itemNumber }) => Number(itemNumber)),
      type: this.type,
      lotCodes: this.lotCodes ? this.lotCodes : [],
      additionalInfo: this.additionalInfo ? this.additionalInfo : '',
    };
  }
}

export enum AlertStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export type RawAlertClaim = Omit<AlertClaim, 'items'> & {
  items: RawAlertClaimItem[] | undefined;
} & RawTimestampedRecord;

export class AlertClaim extends TimestampedRecord {
  public static readonly TYPE_NAME = 'recall';

  // Although it's a number, it's stored as a bigint in the database which wouldn't always fit inside a number type.
  id: string;
  alertId: ProductAlert['id'];
  alert: ProductAlert;
  alertNumber: ProductAlert['alertNumber'];
  disposition: string;
  userId: User['id'];
  status: ClaimStatus;
  notification: boolean;
  assignedToId: string | null;
  user: Pick<User, 'storeId'>;
  items: undefined | AlertClaimItem[];
  returnInvoice: string | null;
  comments: Comment[];
  photos: Photo[];
  itemNumberList?: string;
  itemDescriptionList?: string;
  purchaseNumberList?: string;
  completedBy: string;
  completedByTitle?: string;
  storeEmail?: string;
  storeOwnerName?: string;
  storePhoneNumber?: string;

  public static applyFromRaw(rawClaim: RawAlertClaim, claim: AlertClaim): void {
    Object.assign(claim, rawClaim);
    if (rawClaim.items) {
      claim.itemNumberList = rawClaim.items ? rawClaim.items.map(({itemNumber}) => (itemNumber > 0 ? itemNumber : 'Custom')).join(', ') : '';
      claim.itemDescriptionList = rawClaim.items ? rawClaim.items.map(({ itemName }) => itemName && itemName !== null ? itemName : undefined).join(', ') : '';
      /* tslint:disable-next-line */
      claim.items = rawClaim.items.map((item) => AlertClaimItem.fromRaw(item));
    }
    // Fix the dates with TimestampedRecord's applyFromRaw
    TimestampedRecord.applyFromRaw(rawClaim, claim);
  }

  public static fromRaw(rawClaim: RawAlertClaim): AlertClaim {
    const claim = new AlertClaim();
    AlertClaim.applyFromRaw(rawClaim, claim);
    return claim;
  }

  public static genericToPatch(
    genericPatch: Partial<Claim<Item>>
  ): Partial<Omit<AlertClaim, 'id'>> {
    function mapStatus(
      status: Claim<Item>['status'] | undefined
    ): ClaimStatus | undefined {
      let newStatus: ClaimStatus | undefined;
      switch (status) {
        case 'draft':
          newStatus = ClaimStatus.Draft;
          break;
        case 'validating':
          newStatus = ClaimStatus.Validating;
          break;
        case 'completed':
          newStatus = ClaimStatus.Completed;
          break;
        case 'incomplete':
          newStatus = ClaimStatus.Incomplete;
          break;
        case 'pending':
          newStatus = ClaimStatus.Pending;
          break;
        case 'submitted':
          newStatus = ClaimStatus.Submitted;
          break;
        case 'approved':
          newStatus = ClaimStatus.Approved;
          break;
        default:
          newStatus = undefined;
          break;
      }
      return newStatus;
    }

    /* With a more up-to-date configuration, none of the below block would be needed,
    as assignedToId could be assigned like this:
    assignedToId: genericPatch.assignedTo?.toString(),
    */

    let assignedTo;
    if (genericPatch.assignedTo !== undefined) {
      assignedTo = assignedTo.toString();
    }

    return {
      returnInvoice: genericPatch.returnInvoice,
      assignedToId: assignedTo,
      status: mapStatus(genericPatch.status),
      userId: genericPatch.userId,
    };
  }

  public generateItemsString(items: AlertClaimItem[]) {
    let itemList = '';
    items.forEach((item, index) => {
      itemList += item.itemNumber;
      if (index !== items.length - 1) {
        itemList += ', ';
      }
    });
    return itemList;
  }

  /**
   * Converts this alert claim to a generic claim object.
   * This method is needed because the structure of alert claims is
   * different from those of adjustment and refund claims.
   * This replaces the `transformClaim` function that is used
   * on adjustment and refund claims.
   *
   * Ideally, no transformation would be performed as it comes at a performance cost,
   * however it is required here for backward compatibility.
   */

  public toGenericClaim(isQAAdmin: boolean = false): any {
    const statusText = mapClaimStatus(this.status);
    return {
      type: AlertClaim.TYPE_NAME,
      date: this.created,
      id: this.id,
      createDate: this.created,
      updateDate: this.updated,
      goodsReceivedDate: null,
      photos: this.photos || [], 
      comments: this.comments, // Some areas of code crash if this is not an array.
      items: this.items ? this.items.map((item) => item.toGenericItem()) : [],
      returnInvoice: this.returnInvoice,
      // Confusingly, this and `statusText` have same value in `transformClaim`; we'll do the same here.
      status: this.status,
      statusText,
      storeId: this.user ? this.user.storeId : 2639,
      userId: this.userId,
      assignedTo: this.assignedToId,
      alertNumber: this.alertNumber,
      disposition: this.disposition,
      itemQuantity: this.items
        ? this.items.map((item) => item.units).reduce((a, b) => a + b, 0)
        : 0,
      alert: this.alert,
      alertId: this.alert.alertNumber,
      itemList: this.items ? this.generateItemsString(this.items) : '',
      itemNumberList: this.itemNumberList ? this.itemNumberList : '',
      itemDescriptionList: this.itemDescriptionList ? this.itemDescriptionList : '',
      purchaseNumberList: this.purchaseNumberList ? this.purchaseNumberList : '',
      notification: this.notification,
      completedBy: this.completedBy,
      completedByTitle: this.completedByTitle ? this.completedByTitle : '',
      storeEmail: this.storeEmail ? this.storeEmail : '',
      storeOwnerName: this.storeOwnerName ? this.storeOwnerName : '',
      storePhoneNumber: this.storePhoneNumber ? this.storePhoneNumber : ''
    };
  }
}

export function isAlertClaimType(type: ClaimType): boolean {
  return type === AlertClaim.TYPE_NAME;
}

export type RawAlertClaimItem = AlertClaimItem & RawTimestampedRecord;

export class AlertClaimItem extends TimestampedRecord {
  id: string;
  itemNumber: number;
  itemName: string | undefined;
  claimId: AlertClaim['id'];
  alertNumber: AlertClaim['alertNumber'];
  units: number;
  cases: number;
  reason: string;
  approved: boolean;
  photos: Photo[];

  public static applyFromRaw(
    rawItem: RawAlertClaimItem,
    item: AlertClaimItem
  ): void {
    Object.assign(item, rawItem);
    TimestampedRecord.applyFromRaw(rawItem, item);
  }

  public static fromRaw(rawItem: RawAlertClaimItem): AlertClaimItem {
    const item = new AlertClaimItem();
    AlertClaimItem.applyFromRaw(rawItem, item);
    return item;
  }

  public static genericToPatch(
    genericItemPatch: Partial<Omit<Item, 'id'>>
  ): Partial<Omit<AlertClaimItem, 'id'>> {
    let units: number | undefined;
    if (genericItemPatch.units !== undefined) {
      units = genericItemPatch.units;
    } else {
      units = genericItemPatch.unitsClaimed;
    }
    return {
      approved: !!genericItemPatch.isApproved,
      cases: genericItemPatch.cases,
      units,
      itemNumber: genericItemPatch.itemNumber,
    };
  }

  public toGenericItem(): Item {
    return {
      cases: this.cases,
      units: this.units,
      unitsClaimed: this.units,
      createDate: this.created,
      updateDate: this.updated,
      claimCategory: null,
      isApproved: this.approved,
      id: this.id,
      itemNumber: this.itemNumber,
      alertNumber: this.alertNumber,
      photos: this.photos || [], // TODO RECALLS MODULE: need to implement photos here and in the backend.
      productDescription: this.itemName || '',
      receivalDate: null,
      purchaseNumber: null,
    };
  }
}

export type PaginatedData<T> = {
  results: T[];
  currentPage: number;
  numPages: number;
};

export enum FileType {
  CSV = 'csv',
  PDF = 'pdf',
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/types.ts